import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetData, setResetData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [retrievedData, setRetrievedData] = useState(null);
  
  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
    setMessage('');
  };

  const handleResetChange = (e) => {
    setResetData({
      ...resetData,
      [e.target.name]: e.target.value
    });
  };

  const handleGetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('https://theagencybazaar.com/api/forgot-password', { email });

      console.log('Forgot Password Response:', response.data);

      if (response.data.success) {
        setMessage('Password retrieved successfully!');
        // For demo: show the retrieved data
        if (response.data.demo) {
          setRetrievedData(response.data.demo);
          setShowResetForm(false);
        }
      } else {
        setError(response.data.error || 'Failed to retrieve password');
      }
    } catch (err) {
      console.error('Forgot Password error:', err);
      console.error('Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to retrieve password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!resetData.newPassword || !resetData.confirmPassword) {
      setError('Please enter and confirm your new password');
      return;
    }

    if (resetData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (resetData.newPassword !== resetData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('https://theagencybazaar.com/api/forgot-password/reset', {
        email: email,
        newPassword: resetData.newPassword
      });

      console.log('Reset Password Response:', response.data);

      if (response.data.success) {
        setMessage('Password has been reset successfully! Redirecting to login...');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.error || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Reset Password error:', err);
      console.error('Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid login-container">
      <div className="row justify-content-center">
        <div className="col-lg-6 col-md-8 col-sm-10">
          <div className="card shadow-lg mt-5">
            <div className="card-header bg-warning text-white">
              <h2 className="mb-0 text-center">
                <i className="bi bi-key me-2"></i>
                Forgot Password
              </h2>
            </div>
            
            <div className="card-body">
              {message && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  ✅ {message}
                  <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                </div>
              )}
              
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  ❌ {error}
                  <button type="button" className="btn-close" onClick={() => setError('')}></button>
                </div>
              )}

              {!showResetForm && !retrievedData ? (
                // Email Input Form
                <form onSubmit={handleGetPassword}>
                  <div className="mb-4">
                    <p className="text-muted">
                      Enter your registered email address. We'll help you recover your password.
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="email" className="form-label">
                      <strong>Email Address *</strong>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-envelope"></i>
                      </span>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={handleEmailChange}
                        required
                        className="form-control"
                        placeholder="Enter your registered email"
                      />
                    </div>
                  </div>
                  
                  <div className="d-grid mb-3">
                    <button 
                      type="submit" 
                      disabled={loading}
                      className="btn btn-warning btn-lg"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Get Password
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="text-center mt-4">
                    <button 
                      type="button" 
                      className="btn btn-link text-decoration-none"
                      onClick={() => setShowResetForm(true)}
                    >
                      <i className="bi bi-arrow-repeat me-1"></i>
                      Reset Password Instead?
                    </button>
                  </div>
                </form>
              ) : showResetForm ? (
                // Reset Password Form
                <form onSubmit={handleResetPassword}>
                  <div className="mb-4">
                    <h5 className="text-warning">
                      <i className="bi bi-arrow-repeat me-2"></i>
                      Reset Your Password
                    </h5>
                    <p className="text-muted">
                      For email: <strong>{email || 'Your email'}</strong>
                    </p>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">
                      <strong>New Password *</strong>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-lock"></i>
                      </span>
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={resetData.newPassword}
                        onChange={handleResetChange}
                        required
                        minLength="6"
                        className="form-control"
                        placeholder="Enter new password (min 6 characters)"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label">
                      <strong>Confirm New Password *</strong>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-lock-fill"></i>
                      </span>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={resetData.confirmPassword}
                        onChange={handleResetChange}
                        required
                        className="form-control"
                        placeholder="Confirm your new password"
                      />
                    </div>
                  </div>
                  
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="d-grid">
                        <button 
                          type="submit" 
                          disabled={loading}
                          className="btn btn-warning"
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Resetting...
                            </>
                          ) : (
                            'Reset Password'
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="d-grid">
                        <button 
                          type="button" 
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setShowResetForm(false);
                            setResetData({ newPassword: '', confirmPassword: '' });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : retrievedData && (
                // Password Display (Demo only - Remove in production)
                <div className="text-center">
                  <div className="alert alert-info">
                    <h5 className="alert-heading">
                      <i className="bi bi-info-circle me-2"></i>
                      Demo Mode
                    </h5>
                    <p className="mb-0">
                      In a real application, the password would be sent to your email.
                      For this demo, your password is shown below.
                    </p>
                  </div>
                  
                  <div className="card border-primary mb-4">
                    <div className="card-header bg-primary text-white">
                      <i className="bi bi-shield-check me-2"></i>
                      Account Information
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-6 text-end">
                          <strong>Email:</strong>
                        </div>
                        <div className="col-6">
                          {retrievedData.email}
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-6 text-end">
                          <strong>Password:</strong>
                        </div>
                        <div className="col-6">
                          <code className="bg-light p-2 rounded">{retrievedData.password}</code>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-center gap-3">
                    <button 
                      className="btn btn-success"
                      onClick={() => {
                        setRetrievedData(null);
                        setEmail('');
                        navigate('/login');
                      }}
                    >
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                      Go to Login
                    </button>
                    <button 
                      className="btn btn-outline-primary"
                      onClick={() => {
                        setRetrievedData(null);
                        setShowResetForm(true);
                      }}
                    >
                      <i className="bi bi-arrow-repeat me-2"></i>
                      Reset Password
                    </button>
                  </div>
                </div>
              )}
              
              {/* Navigation Links */}
              <div className="text-center mt-4 pt-3 border-top">
                <div className="row">
                  <div className="col-6">
                    <Link to="/login" className="text-decoration-none">
                      <i className="bi bi-arrow-left me-1"></i>
                      Back to Login
                    </Link>
                  </div>
                  <div className="col-6">
                    <Link to="/register" className="text-decoration-none">
                      <i className="bi bi-person-plus me-1"></i>
                      Create Account
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card-footer text-center bg-light">
              <small className="text-muted">
                <i className="bi bi-shield-exclamation me-1"></i>
                For security reasons, never share your password with anyone.
              </small>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ForgotPassword;


